import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useActor } from '@xstate/react';

import { useNoticeBannersStackContext } from '@sg/garnish';

import { useGlobalAppState } from '../../../../context/GlobalAppState';
import { useDeliveryOrderInFlight } from '../../../../hooks';
import { useReorderNavigation } from '../../../../hooks/useReorderNavigation';
import { useCancelReorder } from '../useCancelReorder';
import { useExecuteReorder } from '../useExecuteReorder';
import { useGroupOrderReorderConfirmation } from '../useGroupOrderReorderConfirmation';
import { useReorderTelemetry } from '../useReorderTelemetry';
import {
  cameFromLocationConfirmation,
  getValidEventKeys,
  handleConflictTelemetry,
  justStartedReorder,
  logger,
  parseConflictResults,
} from '../utils';

import type { ReorderEvent, ReorderStep } from '../../machines/Reorder.machine';

export const useConflictReview = () => {
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const { reorderMachineRef } = useGlobalAppState();
  const [state, sendReorderEvent] = useActor(reorderMachineRef);
  const executeReorder = useExecuteReorder();
  const { isGroupOrderHost, requestGroupOrderReorderConfirmation } =
    useGroupOrderReorderConfirmation();
  const { navigateToConflictReview, replaceWithConflictReview } = useReorderNavigation();
  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  const cancelReorder = useCancelReorder();

  // ─── Telemetry ──────────────────────────────────────────────────
  const {
    startReorderTelemetry,
    continueToBagTelemetry,
    conflictReviewTelemetry,
    cannotReorderTelemetry,
    maxDeliveryTelemetry,
    dressingUnavailableTelemetry,
    missingMenuTelemetry,
  } = useReorderTelemetry();

  return useCallback(
    async (event?: ReorderEvent, options?: Options) => {
      logger.info('Conflict Review', event);

      const reorderStep = options?.reorderStep ?? state.value;

      // Reorder can start as delivery and then change to other types.
      const wasStartedAsDelivery = Boolean(state?.context?.deliveryOrderDetail?.addressId);
      const isReorderingToPickup = event?.orderType === 'pickup';
      const isReorderingToOutpost = event?.orderType === 'outpost';
      const wasStartedAsDeliveryAndNotChanged =
        wasStartedAsDelivery && !isReorderingToPickup && !isReorderingToOutpost;

      // Reorder can start as other types and then change to delivery.
      const isReorderingToDelivery = Boolean(event?.deliveryOrderDetail?.addressId);

      // block delivery reorder if there is an in-flight delivery order.
      const isDeliveryReorder = isReorderingToDelivery || wasStartedAsDeliveryAndNotChanged;

      // blocking reorder should also cancel the reorder flow.
      if (isDeliveryReorder && inFlightDeliveryOrderId) {
        sendReorderEvent('CANCEL_REORDER');
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      // ─── Group Order Confirmation ──────────────────────────────────────────

      const isReorderingLineItem = Boolean(event?.lineItemId ?? state.context.lineItemId);
      const needsGroupOrderReorderConfirmation =
        options?.shouldRequestGroupOrderReorderConfirmation &&
        isGroupOrderHost &&
        !isReorderingLineItem &&
        !options.isGroupOrderReorderConfirmed;

      if (needsGroupOrderReorderConfirmation) {
        requestGroupOrderReorderConfirmation({ reorderEvent: event ?? state.context });

        return;
      }

      // ─── Extra Telemetry Event ─────────────────────────────────────────────
      if (cameFromLocationConfirmation(reorderStep)) {
        continueToBagTelemetry(event ?? state.context);
      }

      // ─── State Machine ──────────────────────────────────────────
      sendReorderEvent({
        ...getValidEventKeys(event),
        type: 'SET_REORDERING',
      });

      // ─── Reorder Mutation ───────────────────────────────────────
      const response = await executeReorder(reorderStep, state.context, event);

      // ─── Conflict Results ───────────────────────────────────────
      const {
        reorderResult,
        conflictType,
        isCannotReorder,
        isMaxDelivery,
        isDressingUnavailable,
        isMissingMenu,
        isUnauthorizedModification,
      } = parseConflictResults(response.data);

      logger.info('Conflict Results', { reorderResult, conflictType });

      // ─── Unauthorized Modification ─────────────────────────────────────────

      if (isUnauthorizedModification) {
        addNoticeBanner({
          text: formatMessage(messages.unauthorizedLocationChange),
          palette: 'caution',
        });

        cancelReorder();

        return;
      }

      // ─── State Machine ──────────────────────────────────────────
      sendReorderEvent({
        ...getValidEventKeys(event),
        type: 'CONFLICT_REVIEW',
        response: response.data as never,
      });

      // ─── Conflict Review Telemetry ──────────────────────────────
      conflictReviewTelemetry({
        ...(event ?? state.context),
        reorderResult,
        conflictType,
      });
      handleConflictTelemetry({
        isCannotReorder,
        isMaxDelivery,
        isDressingUnavailable,
        isMissingMenu,
        cannotReorderTelemetry,
        maxDeliveryTelemetry,
        dressingUnavailableTelemetry,
        missingMenuTelemetry,
      });

      // ─── Just Started Reorder ───────────────────────────────────
      if (justStartedReorder(reorderStep)) {
        // ─── Start Reorder Telemetry ──────────────────────────────
        startReorderTelemetry(event ?? state.context, 'conflict_review', event?.userInitiated);
        navigateToConflictReview();
      }

      // ─── Continuing Reorder ─────────────────────────────────────
      else {
        replaceWithConflictReview();
      }
    },
    [
      state.value,
      state.context,
      inFlightDeliveryOrderId,
      sendReorderEvent,
      executeReorder,
      isGroupOrderHost,
      requestGroupOrderReorderConfirmation,
      conflictReviewTelemetry,
      cannotReorderTelemetry,
      maxDeliveryTelemetry,
      dressingUnavailableTelemetry,
      missingMenuTelemetry,
      replaceWithConflictReview,
      navigateToDeliveryOrderInFlightScreen,
      continueToBagTelemetry,
      startReorderTelemetry,
      navigateToConflictReview,
      addNoticeBanner,
      cancelReorder,
      formatMessage,
    ],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  unauthorizedLocationChange: {
    defaultMessage: "You can't change the location of this order.",
    description: 'Reorder | Unauthorized location change banner',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Options = {
  isGroupOrderReorderConfirmed?: boolean;
  reorderStep?: ReorderStep;
  shouldRequestGroupOrderReorderConfirmation?: boolean;
};
